import React, { type ReactNode } from 'react';

import { styled } from '@compiled/react';
import { FormattedMessage } from 'react-intl-next';

import PersonIcon from '@atlaskit/icon/core/person';
import LegacyPersonIcon from '@atlaskit/legacy-custom-icons/person-icon';
import { Text } from '@atlaskit/primitives/compiled';
import { B50, N20A, N800 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import type { AccountMenuProps } from '../account-menu';
import { ProfilePicture } from '../profile-picture';
import { SkeletonText } from '../skeleton-text';

export type AccountDetailsProps = Pick<
	AccountMenuProps,
	'testId' | 'name' | 'picture' | 'email' | 'managedBy'
>;

export const AccountDetails: React.ComponentType<AccountDetailsProps> = ({
	testId,
	name,
	picture,
	email,
	managedBy,
}) => (
	<Container data-testid={testId}>
		{/* ManagedBy can't go on top of a background since it's background may not be opaque. */}
		{managedBy && (
			<ManagedBy>
				<PersonIconWrapper>
					<PersonIcon
						color="currentColor"
						label=""
						LEGACY_size="small"
						LEGACY_fallbackIcon={LegacyPersonIcon}
					/>
				</PersonIconWrapper>
				<FormattedMessage
					id="identity.account-menu.account-menu-details.managed-account"
					defaultMessage="This account is managed by <b>{managedBy}</b>"
					description="Label showing the user which organisation manages their Atlassian account"
					values={{
						b: (text: ReactNode[]) => (
							<Text as="strong" size="small" color="inherit">
								{text}
							</Text>
						),
						managedBy,
					}}
				/>
			</ManagedBy>
		)}

		<Content hasHeader={!!managedBy}>
			<Profile testId={testId} name={name} picture={picture} email={email} />
		</Content>
	</Container>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.section({
	display: 'flex',
	flexDirection: 'column',
	marginTop: 0,
	marginRight: token('space.100', '8px'),
	marginBottom: 0,
	marginLeft: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ManagedBy = styled.small({
	display: 'box',
	margin: '0px',
	paddingTop: token('space.025', '2px'),
	paddingRight: token('space.100', '8px'),
	paddingBottom: token('space.025', '2px'),
	paddingLeft: token('space.100', '8px'),
	textAlign: 'center',
	borderTopLeftRadius: token('radius.small', '3px'),
	borderTopRightRadius: token('radius.small', '3px'),
	borderBottomLeftRadius: '0',
	borderBottomRightRadius: '0',
	backgroundColor: token('color.background.information', B50),
	color: token('color.text.subtle', '#44546F'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PersonIconWrapper = styled.span({
	marginRight: token('space.050', '4px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > span': {
		width: '12px',
		height: '12px',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Content = styled.div<{ hasHeader: boolean }>(
	{
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: token('elevation.surface.sunken', N20A),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ hasHeader }) => ({
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		borderRadius: hasHeader
			? `0 0 ${token('radius.small', '3px')} ${token('radius.small', '3px')}`
			: token('radius.small', '3px'),
	}),
);

const Profile: React.ComponentType<
	Pick<AccountDetailsProps, 'name' | 'email' | 'picture' | 'testId'>
> = ({ name, email, picture, testId }) => (
	<ProfileWrapper>
		<ProfilePicture src={picture} testId={testId && `${testId}--picture`} />
		<NameAndEmail>
			<DisplayName data-testid={testId && `${testId}--name`}>
				{name || <SkeletonText length="6em" />}
			</DisplayName>
			<EmailAddress data-testid={testId && `${testId}--email`}>
				{email || <SkeletonText length="10em" />}
			</EmailAddress>
		</NameAndEmail>
	</ProfileWrapper>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProfileWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	marginTop: token('space.200', '16px'),
	marginRight: token('space.200', '16px'),
	marginBottom: token('space.200', '16px'),
	marginLeft: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NameAndEmail = styled.div({
	display: 'flex',
	flexDirection: 'column',
	marginTop: 'auto',
	marginRight: 0,
	marginBottom: 'auto',
	marginLeft: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DisplayName = styled.h3({
	color: token('color.text', N800),
	margin: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmailAddress = styled.p({
	marginTop: token('space.025', '2px'),
	color: token('color.text.subtlest', '#626F86'),
	fontWeight: token('font.weight.regular'),
});
